import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/** VocePicker — modale gerarchica Piano dei Conti (C0 → C1 → C2 → C3 foglie) */
export function VocePicker({ open, value, onSelect, onClose }) {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [newConto, setNewConto] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    api.get("/pn/bilancio-voci").then(r => {
      const data = r.data.data || [];
      setAll(data);
      const exp = {};
      data.forEach(v => {
        if (v.c0 != null) exp[`${v.c0}`] = true;
        if (v.c0 != null && v.c1 != null) exp[`${v.c0}/${v.c1}`] = true;
        if (v.c0 != null && v.c1 != null && v.c2 != null) exp[`${v.c0}/${v.c1}/${v.c2}`] = true;
      });
      setExpanded(exp);
    });
  }, [open, reloadKey]);

  const tree = useMemo(() => {
    const byL1 = {};
    all.forEach(v => {
      if (v.c0 == null) return;
      const k0 = v.c0;
      if (!byL1[k0]) byL1[k0] = { node: null, children: {}, leaves: [] };
      if (v.livello === 1) byL1[k0].node = v;
      else if (v.livello === 2 && v.c1 != null) {
        const k1 = v.c1;
        if (!byL1[k0].children[k1]) byL1[k0].children[k1] = { node: null, children: {}, leaves: [] };
        byL1[k0].children[k1].node = v;
      } else if (v.livello === 3 && v.c1 != null && v.c2 != null) {
        if (!byL1[k0].children[v.c1]) byL1[k0].children[v.c1] = { node: null, children: {}, leaves: [] };
        if (!byL1[k0].children[v.c1].children[v.c2]) byL1[k0].children[v.c1].children[v.c2] = { node: null, leaves: [] };
        byL1[k0].children[v.c1].children[v.c2].node = v;
      } else if (v.is_foglia) {
        // Foglia con c2=null → mettila direttamente sotto la famiglia c1
        if (v.c1 != null) {
          if (!byL1[k0].children[v.c1]) byL1[k0].children[v.c1] = { node: null, children: {}, leaves: [] };
          if (v.c2 == null) {
            byL1[k0].children[v.c1].leaves.push(v);
          } else {
            if (!byL1[k0].children[v.c1].children[v.c2]) byL1[k0].children[v.c1].children[v.c2] = { node: null, leaves: [] };
            byL1[k0].children[v.c1].children[v.c2].leaves.push(v);
          }
        } else {
          byL1[k0].leaves.push(v);
        }
      }
    });
    return byL1;
  }, [all]);

  const flatMatches = useMemo(() => {
    if (!search.trim()) return null;
    const s = search.toLowerCase();
    return all.filter(v => v.is_foglia && (
      (v.descrizione || "").toLowerCase().includes(s) || String(v.id_conto).includes(s)
    )).slice(0, 100);
  }, [all, search]);

  const toggle = (k) => setExpanded(e => ({ ...e, [k]: !e[k] }));
  const isOpen = (k) => !!expanded[k];

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-voce-picker"
        style={{ background: "white", width: 720, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0d47a1", color: "white", padding: "8px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>🌲 Piano dei Conti — Seleziona voce</span>
          <input value={search} onChange={e => setSearch(e.target.value)} data-testid="pn-voce-picker-search"
            placeholder="🔍 Cerca conto o descrizione..."
            style={{ flex: 1, padding: "4px 10px", border: "none", fontSize: 12, marginLeft: 8, color: "#0d47a1", background: "white", fontWeight: 600 }} />
          <button onClick={() => setNewConto({ c0: 1, c1: 1, c2: 1, descrizione: "" })} data-testid="pn-voce-picker-new"
            style={{ padding: "4px 12px", background: "#fbc02d", color: "#0d47a1", border: "none", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>+ Nuovo conto</button>
          <button onClick={onClose} style={{ padding: "4px 10px", background: "white", color: "#0d47a1", border: "none", fontWeight: 700, cursor: "pointer" }}>✕ Chiudi</button>
        </div>
        <div style={{ flex: 1, overflow: "auto", fontSize: 12 }}>
          {flatMatches ? (
            <div>
              {flatMatches.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#888" }}>Nessuna voce trovata.</div>}
              {flatMatches.map(v => (
                <div key={v.id_conto} onClick={() => onSelect(v)} data-testid={`pn-voce-flat-${v.id_conto}`}
                  style={{ padding: "6px 14px", cursor: "pointer", borderBottom: "1px solid #eee", background: Number(value) === v.id_conto ? "#bbdefb" : "white" }}
                  onMouseEnter={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "#e3f2fd"; }}
                  onMouseLeave={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "white"; }}>
                  <span style={{ fontFamily: "monospace", color: "#888", marginRight: 8 }}>{v.c0}.{v.c1}.{v.c2}.{v.c3}</span>
                  <b style={{ color: "#0d47a1", fontFamily: "monospace", marginRight: 8 }}>[{v.id_conto}]</b>
                  {v.descrizione}
                </div>
              ))}
            </div>
          ) : (
            <div>
              {Object.keys(tree).sort((a,b) => Number(a) - Number(b)).map(k0 => {
                const n0 = tree[k0];
                const k0open = isOpen(`${k0}`);
                return (
                  <div key={k0}>
                    <div onClick={() => toggle(`${k0}`)} data-testid={`pn-voce-c0-${k0}`}
                      style={{ padding: "8px 14px", cursor: "pointer", background: "#0d47a1", color: "white", fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 14 }}>{k0open ? "▼" : "▶"}</span>
                      {n0.node && <span style={{ fontFamily: "monospace", opacity: 0.85 }}>[{n0.node.id_conto}]</span>}
                      <span>{n0.node ? n0.node.descrizione : `Sezione ${k0}`}</span>
                    </div>
                    {k0open && Object.keys(n0.children).sort((a,b) => Number(a) - Number(b)).map(k1 => {
                      const n1 = n0.children[k1];
                      const k1open = isOpen(`${k0}/${k1}`);
                      return (
                        <div key={k1}>
                          <div onClick={() => toggle(`${k0}/${k1}`)} data-testid={`pn-voce-c1-${k0}-${k1}`}
                            style={{ padding: "6px 14px 6px 32px", cursor: "pointer", background: "#1565c0", color: "white", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ width: 14 }}>{k1open ? "▼" : "▶"}</span>
                            {n1.node && <span style={{ fontFamily: "monospace", opacity: 0.85 }}>[{n1.node.id_conto}]</span>}
                            <span>{n1.node ? n1.node.descrizione : `${k0}.${k1}`}</span>
                            <span style={{ marginLeft: "auto", fontSize: 10, opacity: 0.85 }}>{((n1.leaves||[]).length + Object.values(n1.children).reduce((s,n2) => s + (n2.leaves?.length||0), 0))} conti</span>
                          </div>
                          {/* Foglie direttamente sotto la famiglia (caso c2=null) */}
                          {k1open && (n1.leaves||[]).sort((a,b) => (a.c3||0) - (b.c3||0)).map(v => (
                            <div key={v.id_conto} onClick={() => onSelect(v)} data-testid={`pn-voce-leaf-${v.id_conto}`}
                              style={{ padding: "5px 14px 5px 50px", cursor: "pointer", background: Number(value) === v.id_conto ? "#fff59d" : "white", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 8 }}
                              onMouseEnter={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "#f5f5f5"; }}
                              onMouseLeave={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "white"; }}>
                              <span style={{ fontFamily: "monospace", color: "#888" }}>{v.c0}.{v.c1}.{v.c3 ?? "-"}</span>
                              <b style={{ color: "#0d47a1", fontFamily: "monospace" }}>[{v.id_conto}]</b>
                              <span>{v.descrizione}</span>
                              {Number(value) === v.id_conto && <span style={{ marginLeft: "auto", color: "#1b5e20", fontWeight: 700 }}>✓ selezionato</span>}
                            </div>
                          ))}
                          {k1open && Object.keys(n1.children).sort((a,b) => Number(a) - Number(b)).map(k2 => {
                            const n2 = n1.children[k2];
                            const k2open = isOpen(`${k0}/${k1}/${k2}`);
                            return (
                              <div key={k2}>
                                <div onClick={() => toggle(`${k0}/${k1}/${k2}`)} data-testid={`pn-voce-c2-${k0}-${k1}-${k2}`}
                                  style={{ padding: "5px 14px 5px 50px", cursor: "pointer", background: "#90caf9", color: "#0d47a1", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ width: 14 }}>{k2open ? "▼" : "▶"}</span>
                                  {n2.node && <span style={{ fontFamily: "monospace", color: "#1565c0" }}>[{n2.node.id_conto}]</span>}
                                  <span>{n2.node ? n2.node.descrizione : `${k0}.${k1}.${k2}`}</span>
                                  <span style={{ marginLeft: "auto", fontSize: 10, color: "#444" }}>{(n2.leaves||[]).length} conti</span>
                                </div>
                                {k2open && (n2.leaves||[]).sort((a,b) => (a.c3||0) - (b.c3||0)).map(v => (
                                  <div key={v.id_conto} onClick={() => onSelect(v)} data-testid={`pn-voce-leaf-${v.id_conto}`}
                                    style={{ padding: "5px 14px 5px 70px", cursor: "pointer", background: Number(value) === v.id_conto ? "#fff59d" : "white", borderBottom: "1px solid #eee", display: "flex", alignItems: "center", gap: 8 }}
                                    onMouseEnter={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "#f5f5f5"; }}
                                    onMouseLeave={e => { if (Number(value) !== v.id_conto) e.currentTarget.style.background = "white"; }}>
                                    <span style={{ fontFamily: "monospace", color: "#888" }}>{v.c0}.{v.c1}.{v.c2}.{v.c3}</span>
                                    <b style={{ color: "#0d47a1", fontFamily: "monospace" }}>[{v.id_conto}]</b>
                                    <span>{v.descrizione}</span>
                                    {Number(value) === v.id_conto && <span style={{ marginLeft: "auto", color: "#1b5e20", fontWeight: 700 }}>✓ selezionato</span>}
                                  </div>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ padding: "6px 14px", background: "#f5f5f5", fontSize: 11, color: "#555", borderTop: "1px solid #ddd" }}>
          {flatMatches ? `${flatMatches.length} voci foglia trovate` : `${all.filter(v=>v.is_foglia).length} conti totali · clicca su una sezione per espandere`}
        </div>
      </div>
      {newConto && (
        <NuovoContoModal draft={newConto} setDraft={setNewConto}
          onCreated={(v) => { setNewConto(null); setReloadKey(k => k + 1); if (onSelect) onSelect(v); }}
          onClose={() => setNewConto(null)} />
      )}
    </div>
  );
}

export function NuovoContoModal({ draft, setDraft, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [step, setStep] = useState(1); // 1 = c0, 2 = conto(c1), 3 = sottoconto(foglia)
  const [piano, setPiano] = useState([]);
  const [loadingPiano, setLoadingPiano] = useState(true);
  const [creatingFam, setCreatingFam] = useState(false);
  const [famDescr, setFamDescr] = useState("");

  const reloadPiano = () => api.get("/pn/bilancio-voci").then(r => setPiano(r.data.data || []));
  useEffect(() => { reloadPiano().finally(() => setLoadingPiano(false)); /* eslint-disable-next-line */ }, []);

  // Sezioni livello 1 (Costi/Ricavi)
  const sezioni = piano.filter(v => v.livello === 1).sort((a, b) => (a.c0 ?? 0) - (b.c0 ?? 0));
  const sezioneSel = sezioni.find(s => s.c0 === draft.c0);
  // Conti livello 2 (famiglie) della sezione scelta
  const famiglie = piano.filter(v => v.livello === 2 && v.c0 === draft.c0).sort((a, b) => (a.c1 ?? 0) - (b.c1 ?? 0));
  const famigliaSel = famiglie.find(f => f.c1 === draft.c1);
  // Sottoconti (foglie) già presenti nel conto scelto + prossimo c3 libero
  const foglieFamiglia = piano.filter(v => v.is_foglia && v.c0 === draft.c0 && v.c1 === draft.c1).sort((a, b) => (a.c3 || 0) - (b.c3 || 0));
  const usedC3 = new Set(foglieFamiglia.map(f => f.c3 ?? 0));
  let nextC3 = 1;
  while (usedC3.has(nextC3)) nextC3++;

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const creaFamiglia = async () => {
    if (!famDescr.trim()) { setErr("Descrizione del conto obbligatoria"); return; }
    setSaving(true); setErr(null);
    try {
      const r = await api.post("/pn/bilancio-voci", {
        descrizione: famDescr.trim().toUpperCase(),
        c0: Number(draft.c0), crea_conto: true,
      });
      await reloadPiano();
      set("c1", r.data.c1);
      setCreatingFam(false); setFamDescr("");
      setStep(3);
    } catch (e) { setErr(e.response?.data?.detail || "Errore creazione conto"); }
    finally { setSaving(false); }
  };

  const salva = async () => {
    if (!draft.descrizione?.trim()) { setErr("Descrizione obbligatoria"); return; }
    setSaving(true); setErr(null);
    try {
      const r = await api.post("/pn/bilancio-voci", {
        descrizione: draft.descrizione.trim().toUpperCase(),
        c0: Number(draft.c0), c1: Number(draft.c1), c2: null,
      });
      onCreated(r.data);
    } catch (e) { setErr(e.response?.data?.detail || "Errore creazione"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-newconto-modal" style={{ background: "white", width: 580, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#fbc02d", color: "#0d47a1", padding: "10px 14px", fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <span>+ Nuovo conto nel Piano dei Conti</span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11, opacity: 0.8 }}>Step {step} di 3</span>
        </div>
        {err && <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12, fontWeight: 700 }}>{err}</div>}
        <div style={{ padding: 16, flex: 1, overflow: "auto" }}>
          {loadingPiano ? <div style={{ textAlign: "center", padding: 30, color: "#888" }}>Caricamento...</div> : (
            <>
              {/* Step 1: Costo o Ricavo */}
              {step === 1 && (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "#37474f" }}>Tipo conto?</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {sezioni.map(s => {
                      const isCosto = !(s.descrizione || "").toLowerCase().includes("ricav");
                      const sel = draft.c0 === s.c0;
                      return (
                        <button key={s.c0} onClick={() => { set("c0", s.c0); setStep(2); }} data-testid={`pn-newconto-c0-${s.c0}`}
                          style={{
                            padding: "16px 18px",
                            background: sel ? (isCosto ? "#1565c0" : "#2e7d32") : "white",
                            color: sel ? "white" : (isCosto ? "#1565c0" : "#2e7d32"),
                            border: `2px solid ${isCosto ? "#1565c0" : "#2e7d32"}`,
                            fontSize: 16, fontWeight: 800, cursor: "pointer",
                            textAlign: "left", display: "flex", alignItems: "center", gap: 12,
                          }}>
                          <span style={{ fontSize: 22 }}>{isCosto ? "📤" : "📥"}</span>
                          <span style={{ fontFamily: "monospace", opacity: 0.7 }}>[{s.c0}]</span>
                          <span style={{ flex: 1 }}>{s.descrizione?.toUpperCase()}</span>
                          <span style={{ fontSize: 12, opacity: 0.6 }}>→</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              {/* Step 2: Conto (famiglia livello 2) */}
              {step === 2 && (
                <>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Sezione scelta:</div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "#0d47a1" }}>
                    [{sezioneSel?.c0}] {sezioneSel?.descrizione?.toUpperCase()}
                  </div>
                  {!creatingFam ? (
                    <>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10, color: "#37474f" }}>In quale conto? Scegline uno oppure creane uno nuovo.</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflow: "auto", marginBottom: 10 }}>
                        {famiglie.length === 0 && <div style={{ padding: 16, color: "#888", textAlign: "center" }}>Nessun conto in questa sezione. Creane uno nuovo.</div>}
                        {famiglie.map(f => {
                          const sel = draft.c1 === f.c1;
                          return (
                            <button key={f.c1} onClick={() => { set("c1", f.c1); setStep(3); }} data-testid={`pn-newconto-c1-${f.c1}`}
                              style={{
                                padding: "10px 14px",
                                background: sel ? "#1565c0" : "white",
                                color: sel ? "white" : "#0d47a1",
                                border: "1px solid #1565c0",
                                fontSize: 13, fontWeight: 600, cursor: "pointer",
                                textAlign: "left", display: "flex", alignItems: "center", gap: 10,
                              }}>
                              <span style={{ fontFamily: "monospace", opacity: 0.7, minWidth: 50 }}>{f.c0}.{f.c1}</span>
                              <span style={{ flex: 1 }}>{f.descrizione?.toUpperCase()}</span>
                              <span style={{ fontSize: 12, opacity: 0.7 }}>→</span>
                            </button>
                          );
                        })}
                      </div>
                      <button onClick={() => { setErr(null); setCreatingFam(true); }} data-testid="pn-newconto-newfam-btn"
                        style={{ padding: "10px 14px", background: "#fff8e1", color: "#f57f17", border: "2px dashed #fbc02d", fontSize: 13, fontWeight: 800, cursor: "pointer", width: "100%", textAlign: "left" }}>
                        ➕ Crea un nuovo conto in questa sezione
                      </button>
                    </>
                  ) : (
                    <div style={{ padding: 14, background: "#fff8e1", border: "2px solid #fbc02d" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 6 }}>DESCRIZIONE NUOVO CONTO (livello 2)</div>
                      <input type="text" autoFocus value={famDescr} onChange={e => setFamDescr(e.target.value)} data-testid="pn-newconto-newfam-descr"
                        onKeyDown={e => { if (e.key === "Enter") creaFamiglia(); }}
                        placeholder="es. COSTI SCUOLA"
                        style={{ width: "100%", padding: "10px 14px", border: "2px solid #1565c0", fontSize: 14, fontWeight: 600, textTransform: "uppercase" }} />
                      <div style={{ fontSize: 10, color: "#888", margin: "4px 0 10px" }}>Verrà creato come nuovo conto e potrai subito aggiungere i sottoconti.</div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button onClick={() => { setCreatingFam(false); setFamDescr(""); setErr(null); }}
                          style={{ padding: "6px 14px", background: "white", border: "1px solid #555", cursor: "pointer", fontWeight: 700 }}>← Annulla</button>
                        <span style={{ flex: 1 }} />
                        <button onClick={creaFamiglia} disabled={saving || !famDescr.trim()} data-testid="pn-newconto-newfam-save"
                          style={{ padding: "6px 18px", background: saving || !famDescr.trim() ? "#ccc" : "#1b5e20", color: "white", border: "none", fontWeight: 800, cursor: saving || !famDescr.trim() ? "not-allowed" : "pointer" }}>
                          {saving ? "Creazione..." : "Crea conto e continua →"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Step 3: Sottoconto (foglia) */}
              {step === 3 && (
                <>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Conto scelto:</div>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: "#0d47a1" }}>
                    {draft.c0}.{draft.c1} · {famigliaSel?.descrizione?.toUpperCase()}
                  </div>
                  {foglieFamiglia.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", marginBottom: 4 }}>Sottoconti già presenti in questo conto:</div>
                      <div style={{ maxHeight: 140, overflow: "auto", border: "1px solid #eee" }}>
                        {foglieFamiglia.map(v => (
                          <div key={v.id_conto} data-testid={`pn-newconto-existing-${v.id_conto}`}
                            style={{ padding: "4px 10px", borderBottom: "1px solid #f0f0f0", fontSize: 12, display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontFamily: "monospace", color: "#888" }}>{v.c0}.{v.c1}.{v.c3}</span>
                            <b style={{ color: "#0d47a1", fontFamily: "monospace" }}>[{v.id_conto}]</b>
                            <span>{v.descrizione}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>Nuovo sottoconto, sarà creato come:</div>
                  <div style={{ padding: 14, background: "#fff8e1", border: "2px solid #fbc02d", marginBottom: 14 }}>
                    <div style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: "#0d47a1", marginBottom: 6 }}>
                      {draft.c0}.{draft.c1}.{nextC3}
                    </div>
                    <div style={{ fontSize: 11, color: "#555" }}>
                      <b>{sezioneSel?.descrizione?.toUpperCase()}</b> → <b>{famigliaSel?.descrizione?.toUpperCase()}</b> → <i>nuovo sottoconto</i>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#37474f", marginBottom: 6 }}>DESCRIZIONE NUOVO SOTTOCONTO</div>
                    <input type="text" autoFocus value={draft.descrizione || ""} onChange={e => set("descrizione", e.target.value)} data-testid="pn-newconto-descr"
                      onKeyDown={e => { if (e.key === "Enter") salva(); }}
                      placeholder="es. CANCELLERIA E CONSUMABILI"
                      style={{ width: "100%", padding: "10px 14px", border: "2px solid #1565c0", fontSize: 14, fontWeight: 600, textTransform: "uppercase" }} />
                    <div style={{ fontSize: 10, color: "#888", marginTop: 4 }}>Verrà salvato in maiuscolo.</div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div style={{ padding: 10, display: "flex", gap: 8, alignItems: "center", background: "#fafafa", borderTop: "1px solid #eee" }}>
          {step > 1 && (
            <button onClick={() => { setStep(step - 1); setCreatingFam(false); setErr(null); }} data-testid="pn-newconto-back"
              style={{ padding: "6px 14px", background: "white", border: "1px solid #555", cursor: "pointer", fontWeight: 700 }}>← Indietro</button>
          )}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: "6px 14px", background: "white", border: "1px solid #555", cursor: "pointer" }}>Annulla</button>
          {step === 3 && (
            <button onClick={salva} disabled={saving || !draft.descrizione?.trim()} data-testid="pn-newconto-save"
              style={{ padding: "6px 18px", background: saving || !draft.descrizione?.trim() ? "#ccc" : "#1b5e20", color: "white", border: "none", fontWeight: 800, fontSize: 13, cursor: saving || !draft.descrizione?.trim() ? "not-allowed" : "pointer" }}>
              {saving ? "Creazione..." : "💾 Crea conto"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
